import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardTitle, IconButton, Input, Spinner } from '@wundo/ui';
import {
  type CohortPromptFields,
  createCohortPrompt,
  deleteCohortPrompt,
  fetchCohortPrompt,
  updateCohortPrompt,
} from '@/lib/cohorts.ts';
import type { CohortPrompt } from '@/types/cohorts.ts';
import {
  compileSystemPrompt,
  PROMPT_FIELDS,
  type PromptField,
  type PromptFieldKey,
  promptFieldsOf,
} from './promptFields.ts';
import { reportError } from '@/lib/reportError.ts';
import { X } from 'lucide-react';

/**
 * What this cohort adds to the system prompt, and what that adds up to. Every
 * edit saves itself: each list is a handful of one-line facts, and a submit
 * would make adding one two acts. Emptying all four deletes the record.
 */
export function CohortPromptTab({ cohortId }: { cohortId: string }) {
  const {
    data: prompt,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['cohorts', cohortId, 'prompt'],
    queryFn: () => fetchCohortPrompt(cohortId),
  });

  if (isPending) {
    return (
      <div className='flex justify-center py-7'>
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <p
        role='alert'
        className='rounded-sm bg-danger-fill px-3.5 py-2.5 text-sm font-semibold text-danger'
      >
        Failed to load prompt settings
      </p>
    );
  }

  /*
   * Keyed by the record, so the editor's own copy of the four lists is seeded
   * from the server once and then belongs to the editor. It reseeds only when
   * this cohort's prompt starts existing, or stops.
   */
  return <PromptEditor key={prompt?.id ?? 'none'} cohortId={cohortId} prompt={prompt} />;
}

function PromptEditor({
  cohortId,
  prompt,
}: {
  cohortId: string;
  prompt: CohortPrompt | null;
}) {
  const queryClient = useQueryClient();
  const [fields, setFields] = useState<CohortPromptFields>(() => promptFieldsOf(prompt));
  const [saving, setSaving] = useState<PromptFieldKey | null>(null);
  const [error, setError] = useState('');

  async function save(next: CohortPromptFields, field: PromptFieldKey) {
    setSaving(field);
    setError('');

    try {
      const hasContent = PROMPT_FIELDS.some((entry) => next[entry.key].length > 0);
      const key = ['cohorts', cohortId, 'prompt'];

      if (hasContent) {
        const saved = prompt
          ? await updateCohortPrompt(prompt.id, next)
          : await createCohortPrompt(cohortId, next);
        queryClient.setQueryData(key, saved);
      } else if (prompt) {
        await deleteCohortPrompt(prompt.id);
        queryClient.setQueryData(key, null);
      }
    } catch (failure) {
      reportError(failure, { where: 'CohortPromptTab.save', cohortId, field });
      setError(`Failed to save ${field}`);
    } finally {
      setSaving(null);
    }
  }

  function updateItem(field: PromptFieldKey, index: number, value: string) {
    setFields((current) => ({
      ...current,
      [field]: current[field].map((item, at) => (at === index ? value : item)),
    }));
  }

  function removeItem(field: PromptFieldKey, index: number) {
    const next = { ...fields, [field]: fields[field].filter((_, at) => at !== index) };
    setFields(next);
    void save(next, field);
  }

  function addItem(field: PromptFieldKey, value: string) {
    if (value === '' || fields[field].includes(value)) return;

    const next = { ...fields, [field]: [...fields[field], value] };
    setFields(next);
    void save(next, field);
  }

  return (
    <div className='flex flex-col gap-5'>
      {error !== '' && (
        <p
          role='alert'
          className='rounded-sm bg-danger-fill px-3.5 py-2.5 text-sm font-semibold text-danger'
        >
          {error}
        </p>
      )}

      <Card tone='quiet'>
        <CardTitle>System Prompt Configuration</CardTitle>
        <ul className='mt-2 flex list-disc flex-col gap-1 pl-5 text-sm text-ink-secondary'>
          <li>
            These modifications will be integrated into the system prompt for every user
            message.
          </li>
          <li>
            Keep changes minimal—excessive modifications increase the risk of
            hallucinations and reduced focus.
          </li>
        </ul>
      </Card>

      <div className='grid grid-cols-2 gap-5 max-agenda:grid-cols-1'>
        <div className='flex flex-col gap-5'>
          {PROMPT_FIELDS.map((field) => (
            <PromptFieldCard
              key={field.key}
              field={field}
              items={fields[field.key]}
              saving={saving === field.key}
              onAdd={(value) => addItem(field.key, value)}
              onItemChange={(index, value) => updateItem(field.key, index, value)}
              onCommit={() => void save(fields, field.key)}
              onRemove={(index) => removeItem(field.key, index)}
            />
          ))}
        </div>

        <Card as='section' aria-label='Prompt Preview'>
          <CardTitle>Prompt Preview</CardTitle>
          <pre className='mt-3.5 rounded-md bg-fill-faint p-3.5 font-mono text-sm break-words whitespace-pre-wrap text-ink-secondary'>
            {compileSystemPrompt(fields)}
          </pre>
        </Card>
      </div>
    </div>
  );
}

interface PromptFieldCardProps {
  field: PromptField;
  items: string[];
  /** Whether this list is being saved; it is locked meanwhile. */
  saving: boolean;
  /** Adds a line, already trimmed; the editor decides whether to keep it. */
  onAdd: (value: string) => void;
  onItemChange: (index: number, value: string) => void;
  /** Saves the list as it stands, when a line is left. */
  onCommit: () => void;
  onRemove: (index: number) => void;
}

function PromptFieldCard({
  field,
  items,
  saving,
  onAdd,
  onItemChange,
  onCommit,
  onRemove,
}: PromptFieldCardProps) {
  /* What is typed in the new line; undefined while no line is being added. */
  const [adding, setAdding] = useState<string | undefined>(undefined);

  function add() {
    const value = (adding ?? '').trim();
    setAdding(undefined);
    onAdd(value);
  }

  return (
    <Card as='section' aria-label={field.title}>
      <div className='mb-3.5 flex items-center justify-between gap-2'>
        <CardTitle>{field.title}</CardTitle>
        {saving && (
          <span
            role='status'
            className='flex items-center gap-1.5 text-xs text-ink-muted'
          >
            <Spinner className='size-3' />
            Saving...
          </span>
        )}
      </div>

      <div className='flex flex-col gap-2'>
        {items.map((item, index) => (
          <div
            key={`${field.key}-${index}`}
            className='flex items-center gap-2 rounded-md bg-fill-faint p-2'
          >
            <Input
              variant='bare'
              aria-label={`${field.title} ${index + 1}`}
              value={item}
              maxLength={250}
              minLength={5}
              disabled={saving}
              onChange={(event) => onItemChange(index, event.target.value)}
              onBlur={onCommit}
              onKeyUp={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
            />
            <IconButton
              aria-label={`Remove ${field.title.toLowerCase()} ${index + 1}`}
              size='sm'
              variant='danger'
              disabled={saving}
              onClick={() => onRemove(index)}
            >
              <X className='size-4' aria-hidden='true' />
            </IconButton>
          </div>
        ))}

        {adding !== undefined ? (
          <div className='flex items-center gap-2 rounded-md bg-fill-faint p-2'>
            <Input
              variant='bare'
              autoFocus
              aria-label={field.addButtonText}
              placeholder={field.placeholder}
              value={adding}
              maxLength={250}
              minLength={5}
              onChange={(event) => setAdding(event.target.value)}
              onKeyUp={(event) => {
                if (event.key === 'Enter') add();
                if (event.key === 'Escape') setAdding(undefined);
              }}
            />
            <Button size='sm' variant='quiet' onClick={() => setAdding(undefined)}>
              Cancel
            </Button>
          </div>
        ) : (
          <div>
            <Button
              size='sm'
              variant='quiet'
              disabled={saving}
              onClick={() => setAdding('')}
            >
              {field.addButtonText}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
